'use client';

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import Link from 'next/link';
import { LocationHydration } from 'stores/LocationStore';
import { Authentication } from 'components/Session';
import { useScroll } from 'hooks/useScroll';
import IconSymplaMinimal from 'assets/icons/logo-sympla-minimal.svg';
import { isMobile as getIsMobile } from 'utils/misc';
import IconSympla from 'assets/icons/logo-sympla.svg';
import NewHeaderContext from '../NewHeaderContext/NewHeaderContext';
import { MobileCTACreateEventBanner } from '../MobileCTACreateEventBanner/MobileCTACreateEventBanner';
import LocationButton from '../LocationButton/LocationButton';
import * as styles from './Navbar.css';

interface NavbarProps {
  userId: number | null;
  locationHydration: LocationHydration;
  alwaysCollapsed?: boolean;
  isMobile?: boolean;
}

const minHeight = {
  navbarCollapsedDesktop: 92,
  baseDesktop: 232,
  baseMobile: 216,
};

function Navbar({
  userId,
  locationHydration,
  alwaysCollapsed = false,
  isMobile = false,
}: NavbarProps) {
  const isNavbarCollapsed = useScroll(alwaysCollapsed);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const paddingInitial = useMemo(() => {
    if (isMobile) return minHeight.baseMobile;
    return isNavbarCollapsed
      ? minHeight.navbarCollapsedDesktop
      : minHeight.baseDesktop;
  }, [isNavbarCollapsed, isMobile]);
  const [navPadding, setNavPadding] = useState(paddingInitial);
  const headerElement = headerRef.current;

  const updateHeight = useCallback(() => {
    const heightElement = headerRef.current?.offsetHeight || paddingInitial;
    setNavPadding(heightElement);
  }, [paddingInitial]);

  useEffect(() => {
    if (headerElement) updateHeight();
  }, [isNavbarCollapsed, isMobile]);

  useEffect(() => {
    const isMobileWindow = getIsMobile();
    if (isMobileWindow !== isMobile && isNavbarCollapsed) updateHeight();
  }, []);

  return (
    <header>
      <nav
        ref={headerRef}
        className={styles.navbar}
        onTransitionEnd={updateHeight}
      >
        <MobileCTACreateEventBanner
          isMobile={isMobile}
          handleCloseBanner={updateHeight}
        />

        <div
          className={`${styles.container} ${
            isNavbarCollapsed ? styles.containerCollapsed : ''
          }`}
        >
          <Link
            href="/"
            aria-label="Sympla sua plataforma de eventos. Logo."
            title="Sympla sua plataforma de eventos"
            className={styles.LinkLogo}
            data-testid="header-navbar-logo"
          >
            <div className={styles.containerMobile}>
              <IconSymplaMinimal
                width={24}
                height={24}
                className={styles.LogoStyle}
                alt="Sympla sua plataforma de eventos"
              />
            </div>
            <div className={styles.containerDesktop}>
              <div hidden={!isNavbarCollapsed}>
                <IconSymplaMinimal
                  width={38}
                  height={38}
                  className={styles.LogoStyle}
                  alt="Sympla sua plataforma de eventos"
                  hidden={!isNavbarCollapsed}
                />
              </div>
              <div hidden={isNavbarCollapsed}>
                <IconSympla
                  width={112}
                  height={48}
                  className={`${styles.containerDesktop} ${styles.LogoStyle}`}
                  alt="Sympla sua plataforma de eventos"
                  hidden={isNavbarCollapsed}
                />
              </div>
            </div>
          </Link>

          <div className={styles.containerMobile}>
            <LocationButton
              selectedLocation={locationHydration.selectedLocation}
              variant="default"
            />
          </div>

          <div
            className={`${styles.filtersContainer} ${
              isNavbarCollapsed ? styles.filtersContainerCollapsed : ''
            } ${styles.containerDesktop}`}
            data-testid="header-navbar-search-desktop"
          >
            <NewHeaderContext
              locationHydration={locationHydration}
              isNavbarCollapsed={isNavbarCollapsed}
            />
          </div>

          <div style={{ zIndex: 1 }} data-testid="header-navbar-auth">
            <Authentication
              isAuthenticated={!!userId}
              isNavbarCollapsed={isNavbarCollapsed}
            />
          </div>
        </div>

        <div
          className={`${styles.container} ${styles.filtersContainer} ${
            styles.containerMobile
          }  ${isNavbarCollapsed ? styles.containerCollapsed : ''}`}
          data-testid="header-navbar-search-mobile"
        >
          <NewHeaderContext
            locationHydration={locationHydration}
            isNavbarCollapsed={isNavbarCollapsed}
          />
        </div>
      </nav>

      <div style={{ paddingTop: `${navPadding}px`, transition: 'all .3s' }} />
    </header>
  );
}

export default Navbar;
